import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Typography, LinearProgress, CircularProgress, Tooltip, IconButton } from "@mui/material";
import { Modal, notification } from "antd";
import CloseIcon from '@mui/icons-material/Close';
import moment from "moment";

import { addEditJobShiftPlans, getJobShiftPlanDetails } from "../../../../actions/v2/CalendarActions";
import { Strings } from "../../../../dataProvider/localize";
import { capitalizeFirst, plannedShiftTypes } from "../../../../utils/common";
import PlanJobShiftsForm from "./PlanJobShiftsForm";
import DeleteShiftPlan from "./DeleteShiftPlan";
import TableView from "../../../../components/v2/views/table/TableView";

import v2Styles from "../../v2Styles.module.css";

export default function PlanJobShiftsModal({ tasks, editingPlanId, job, onClose }) {

  const [dateEvents, setDateEvents] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [formInitials, setFormInitials] = useState({ start_time: moment("00:00", "HH:mm"), end_time: moment("00:00", "HH:mm"), staff: 1, shift_type: plannedShiftTypes.Fixed })
  const [deletePlan, setDeletePlan] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);

  const onSubmit = (event) => {
    setFormInitials({
      start_time: moment("00:00", "HH:mm"),
      end_time: moment("00:00", "HH:mm"),
      staff: 1,
      shift_type: plannedShiftTypes.Fixed
    });
    if (event) {
      if (event.id) {
        setDateEvents(v => v.map(x => x.id === event.id ? { ...event } : x))
      } else {
        setDateEvents(v => [...v, { ...event, id: `temp_${new Date().getTime()}` }]);
      }
    }
  }
  const controller = useRef();

  const dispatch = useDispatch();

  const onCancel = () => {
    onClose();
  }

  const onConfirmPlans = () => {
    setSubmitting(true);
    let formData = {
      plans: dateEvents.map(x => ({
        ...(editingPlanId && editingPlanId === x.id ? { id: x.id } : {}),
        job_id: job.job_id,
        task_ids: x.tasks.map(v => v.value),
        duration: moment(x.duration).format("HH:mm"),
        start_time: moment(x.start_time).format("HH:mm:00"),
        end_time: moment(x.end_time).format("HH:mm:00"),
        applicable_start_date: moment(x.applicable_period[0]).format("YYYY-MM-DD"),
        applicable_end_date: moment(x.applicable_period[1]).format("YYYY-MM-DD"),
        copy_across: x.copy_across.toString().toLowerCase(),
        shift_type: x.shift_type && x.shift_type.value ? x.shift_type.value : null,
        ...(x.instance_day && x.copy_across === "Instance" ? { instance_day: x.instance_day.value } : {}),
        staff_count: x.staff
      }))
    }
    if (editingPlanId) {
      formData = formData.plans[0]
    }
    dispatch(addEditJobShiftPlans(formData, editingPlanId ? "Edit" : "Add")).then(message => {
      notification.success({
        message: Strings.success_title,
        description: message ? message : Strings.generic_error, onClick: () => { },
        className: 'ant-success'
      });
      setSubmitting(false);
      onClose();
    }).catch(err => {
      setSubmitting(false);
      notification.error({
        message: Strings.error_title,
        description: err ? err : Strings.generic_error,
        className: 'ant-error'
      })
    })
  }

  useEffect(() => {
    if (editingPlanId) {
      getPlanDetails(editingPlanId);
    }
  }, [editingPlanId])

  const getPlanDetails = (planId) => {
    setLoadingEvents(true);
    controller.current = new AbortController();
    dispatch(getJobShiftPlanDetails(planId, { signal: controller.current.signal })).then(res => {
      setLoadingEvents(false);
      if (res.shift_plan) {
        const selectedTasks = tasks.filter(x => res.shift_plan.task_ids.indexOf(x.id) !== -1).map(v => ({ label: v.task_name, value: v.id }));
        const data = {
          tasks: selectedTasks,
          applicable_period: [moment.utc(res.shift_plan.applicable_start_date), moment.utc(res.shift_plan.applicable_end_date)],
          start_time: moment(res.shift_plan.start_time, "HH:mm"),
          end_time: moment(res.shift_plan.end_time, "HH:mm"),
          duration: moment(res.shift_plan.duration, "HH:mm"),
          staff: res.shift_plan.staff_count ? res.shift_plan.staff_count : 1,
          id: res.shift_plan.id,
          copy_across: capitalizeFirst(res.shift_plan.copy_across, true, false),
          shift_type: res.shift_plan.shift_type && plannedShiftTypes[res.shift_plan.shift_type] ? plannedShiftTypes[res.shift_plan.shift_type] : null,
          ...(res.shift_plan.instance_day ? { instance_day: { label: res.shift_plan.instance_day, value: res.shift_plan.instance_day } } : {})
        }
        setDateEvents([{ ...data }])
        setFormInitials({ ...data });
      }
    }).catch(err => {
      if (err) {
        setLoadingEvents(false);
        notification.error({
          message: Strings.error_title,
          description: err ? err : "There was an error while getting Shift Details.",
          className: "ant-error"
        });
        onClose();
      }
    })
  }

  const onEdit = (shift) => {
    setFormInitials({ ...shift });
  }

  const onFormCancel = () => {
    setFormInitials({
      start_time: moment("00:00", "HH:mm"),
      end_time: moment("00:00", "HH:mm"),
      staff: 1,
      shift_type: plannedShiftTypes.Fixed
    });
  }

  const onDeleteShift = (shift) => {
    if (shift.id && shift.id.toString().indexOf("temp") === -1) {
      setSelectedShift(shift)
      setDeletePlan(true);
    } else {
      setFormInitials({ start_time: moment("00:00", "HH:mm"), end_time: moment("00:00", "HH:mm"), staff: 1, shift_type: plannedShiftTypes.Fixed });
      setDateEvents(v => v.filter(x => x.id !== shift.id));
    }
  }

  const onDeleteClose = () => {
    setDeletePlan(false);
    onClose();
  }

  return (
    <div className="v2-modal-container">
      {loadingEvents ?
        <div className="d-flex justify-content-center">
          <CircularProgress size={20} />
        </div> : (
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className={v2Styles.ModalHeading}>Plan Shifts</div>
                <div className="">
                  <hr className="v3-modal-hr" />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-12">
                <PlanJobShiftsForm isUpdate={editingPlanId ? true : false} initialValues={formInitials} tasks={tasks} onSubmit={onSubmit} onFormCancel={onFormCancel} />
              </div>
            </div>
            <div style={{ maxHeight: "50vh", overflow: "hidden auto" }}>
              <PlanJobShiftsTable onDeleteShift={onDeleteShift} onEdit={onEdit} isUpdate={editingPlanId ? true : false} tasks={tasks} plannings={dateEvents} />
            </div>
            <div className="row">
              <div className="col-12 d-flex align-items-center justify-content-end" style={{ gap: "10px" }}>
                {submitting ? (<CircularProgress size={20} />) : null}
                <button type="button" onClick={onCancel} className={v2Styles.OutlinedButton}>Cancel</button>
                <button type="button" onClick={onConfirmPlans} disabled={submitting || !dateEvents.length} className={v2Styles.PrimaryButton}>Confirm Shifts</button>
              </div>
            </div>
          </div>
        )}
      <Modal
        className="cs-modal-v2"
        centered
        footer={null}
        visible={deletePlan}
        maskClosable={false}
        onCancel={() => { setDeletePlan(false); }}
        afterClose={() => { setSelectedShift(null) }}
        destroyOnClose={true}
        width={"600px"}
        bodyStyle={{ paddingRight: "0px", paddingLeft: "0px" }}
        closeIcon={<div className={v2Styles.ModalCloseIcon}><CloseIcon /></div>}
      >
        <DeleteShiftPlan selectedPlan={selectedShift} onClose={() => setDeletePlan(false)} onSubmitClose={() => onDeleteClose()} />
      </Modal>
    </div>
  )
}

function PlanJobShiftsTable({ tasks, plannings, isLoading, isDisabled, onEdit, onDeleteShift }) {

  const [submitting, setSubmitting] = useState(false);

  const customTaskView = (columnName, value) => {
    return (
      <div className="d-flex" style={{ flexWrap: "wrap", gap: "5px" }}>
        {value && value.length ? value.map(x => (
          <Typography title={x.label}
            key={x.value}
            noWrap
            className={v2Styles.TableText}
            style={{ background: "#e2e2e2", padding: "0px 6px", borderRadius: "2px" }}>
            {x.label}
          </Typography>
        )) : null}
      </div>
    )
  }

  const ActionsView = (column, value, rowData) => {
    const loadingRow = false;

    return (
      <div className="d-flex align-items-center justify-content-center">
        {!isDisabled ? (
          <>
            <div className="d-flex align-items-center justify-content-center" style={{ gap: "5px" }}>
              <CircularProgress size={20} hidden={loadingRow !== rowData.id} />
              <Tooltip title="Edit Shift Plan">
                <span>
                  <IconButton disabled={submitting || isLoading} className="px-1" sx={{ height: "auto", color: "#90bf00", fontSize: "14px" }} size="small" onClick={() => { onEdit(rowData) }}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" height="1em">
                      <path fill="currentColor" d="M362.7 19.3L314.3 67.7 444.3 197.7l48.4-48.4c25-25 25-65.5 0-90.5L453.3 19.3c-25-25-65.5-25-90.5 0zm-71 71L58.6 323.5c-10.4 10.4-18 23.3-22.2 37.4L1 481.2C-1.5 489.7 .8 498.8 7 505s15.3 8.5 23.7 6.1l120.3-35.4c14.1-4.2 27-11.8 37.4-22.2L421.7 220.3 291.7 90.3z" />
                    </svg>
                  </IconButton>
                </span>
              </Tooltip>
              {/* <div> */}
              <Tooltip title="Delete Shift Plan">
                <span>
                  <IconButton disabled={submitting || isLoading} sx={{ height: "auto", color: "#ff0000", fontSize: "14px" }} size="small" onClick={() => { onDeleteShift(rowData) }}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" height="1em" >
                      <path fill="currentColor" d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z" />
                    </svg>
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </>) : null}
      </div>
    )
  }

  const CopyAcrossView = (column, value, row) => {
    return value === "Instance" && row.instance_day && row.instance_day.label ? `Instance - ${row.instance_day.label}` : value
  }

  const tableConfig = {
    rowKey: "id",
    columns: [
      { columnName: "Task", keyName: "tasks", columnClassName: "align-top", customView: customTaskView, width: "20%", getValue: (v) => v.map(x => x.label).join(", ") },
      { columnName: "Applicable Period", keyName: "applicable_period", width: "20%", columnClassName: "text-center align-top", textAlign: "center", getValue: (v) => v && v.length === 2 ? `${moment.utc(v[0]).format("DD/MM/YYYY")} to ${moment.utc(v[1]).format("DD/MM/YYYY")}` : "" },
      { columnName: "Start Time", keyName: "start_time", width: "10%", columnClassName: "text-center align-top", textAlign: "center", getValue: (v) => moment(v).format("hh:mm A") },
      { columnName: "End Time", keyName: "end_time", width: "10%", columnClassName: "text-center align-top", textAlign: "center", getValue: (v) => moment(v).format("hh:mm A") },
      { columnName: "Staff", keyName: "staff", width: "8%", columnClassName: "align-top" },
      { columnName: "Copy Across", keyName: "copy_across", width: "15%", columnClassName: "align-top", customView: CopyAcrossView },
      { columnName: "Actions", keyName: "actions", width: "10%", columnClassName: "align-top text-center", customView: ActionsView },
    ]
  }

  return (
    <div className="row">
      <div className="col-12 py-3">
        <div style={{ height: "4px" }}>
          {isLoading ? (<LinearProgress />) : null}
        </div>
        <TableView rowData={plannings} dataTable={tableConfig} parentLoading={false} />
      </div>
    </div>
  )
}